import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { TPlayListCreateForm } from '../modals/video/playlist-create-modal';
import type { AssetKind } from '../pages/module1-data';
import {
  TDynamicData,
  TDynamicFilters,
  TExcelDownloadPayload,
} from '../state/analytics/dynamicsSlice';
import { TFinancePlan, TInvoice, TPaymentData } from '../types/finance';
import {
  TAnalyticsFilter,
  TAnalyticsResponse,
} from '../types/responses/analytics';
import { TUser } from '../types/responses/users';
import {
  TCreatePreviewPayload,
  TCreatePreviewResponse,
  TCreatedVideo,
  TLinkChunkStat,
  TLinkForm,
  TLinkListed,
  TLinkStat,
  TListedPlaylist,
  TPlayListEditForm,
  TPlayListItem,
  TPlaylistContentItem,
  TPlaylistQueryParam,
  TUploadByLinkResponse,
  TUploadVideoResponse,
  TVideoCreateForm,
  TVideoCreatePayload,
  TVideoResponse,
} from '../types/responses/video';
import {
  TAuthResponse,
  TForgotLoginForm,
  TForgotPasswordCompletePayload,
  TLoginForm,
  TMeResponse,
} from '../types/users';

export type TAssetLink = {
  id: string;
  s3Key: string;
  name: string;
  title: string | null;
  description: string | null;
  settings: Partial<TLinkForm> | null;
  kind: AssetKind;
  size: string | null;
  isAuthRequired: boolean;
  viewsCount: number;
  viewingDuration: number;
  createdAt?: string;
};

export type TAssetLinkStats = {
  assetLink: Omit<TAssetLink, 'createdAt' | 'isAuthRequired'>;
  viewsCount: number;
  viewingDuration: number;
  uniqueViewersCount: number;
  eventsCount: number;
  indexEventsCount: number;
  documentSummary?: {
    sessionsCount: number;
    completedSessionsCount: number;
    maximumCoveragePercent: number;
    maximumQualifiedDuration: number;
  };
  documentSessions?: {
    sessionId: string;
    coveragePercent: number;
    qualifiedDurationMs: number;
    minimumQualifiedDurationMs: number;
    bottomReached: boolean;
    speed: number;
    isPlaying: boolean;
    pauseReason: string | null;
    completed: boolean;
    lastEventAt: string;
    userId: string | null;
    userEmail?: string | null;
    userFullName?: string | null;
  }[];
  events: {
    id: string;
    assetLinkId: string;
    userId: string | null;
    type: string;
    viewingDuration: number;
    indexes: Record<string, unknown> | null;
    metadata: Record<string, unknown> | null;
    createdAt: string;
  }[];
};

export type TEducationAsset = {
  s3Key: string;
  name: string;
  kind: AssetKind;
  sizeBytes: number;
  minimumQualifiedDurationMs: number | null;
  updatedAt: string | null;
};

export const BASE_API_PATH = process.env.REMOTE_HOST || '/api/'; // Default to proxy path if host is missing

export const TOKEN_LS_PATH = 'plai-access';

const ASSIGNED_PLAYLISTS_CACHE_MS = 30_000;

const baseAPIInstance = axios.create({
  baseURL: BASE_API_PATH,
});

let assignedPlaylistsCache: {
  response: AxiosResponse<TListedPlaylist[]>;
  expiresAt: number;
} | null = null;
let assignedPlaylistsRequest: Promise<AxiosResponse<TListedPlaylist[]>> | null =
  null;

baseAPIInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_LS_PATH);

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (localStorage.debugVideoPlayer === 'true') {
    config.headers['X-Debug-Video-Player'] = 'true';
  }

  return config;
});

export const API = {
  streams: {
    getEducationAssets: () =>
      baseAPIInstance.get('/streams/education-assets') as Promise<
        AxiosResponse<TEducationAsset[]>
      >,
  },
  assetLinks: {
    create: (data: {
      s3Key: string;
      name: string;
      title?: string;
      description?: string;
      settings?: Partial<TLinkForm>;
      kind: AssetKind;
      size?: string;
      isAuthRequired?: boolean;
    }) => {
      return baseAPIInstance.post('/asset-link', data) as Promise<
        AxiosResponse<TAssetLink>
      >;
    },
    get: (id: string, config?: AxiosRequestConfig) => {
      return baseAPIInstance.get(`/asset-link/${id}`, config) as Promise<
        AxiosResponse<TAssetLink>
      >;
    },
    getSelf: () => {
      return baseAPIInstance.get('/asset-link/self') as Promise<
        AxiosResponse<TAssetLink[]>
      >;
    },
    getByAssetKey: (s3Key: string, config?: AxiosRequestConfig) => {
      return baseAPIInstance.get('/asset-link/by-asset', {
        ...config,
        params: { key: s3Key },
      }) as Promise<AxiosResponse<TAssetLink[]>>;
    },
    getStats: (id: string) => {
      return baseAPIInstance.get(`/asset-link/${id}/stats`) as Promise<
        AxiosResponse<TAssetLinkStats>
      >;
    },
    delete: (id: string) => {
      return baseAPIInstance.delete(`/asset-link/${id}`) as Promise<
        AxiosResponse<void>
      >;
    },
    createEvent: (
      id: string,
      data: {
        type: 'open' | 'heartbeat' | 'close' | 'index';
        viewingDuration?: number;
        indexes?: Record<string, unknown>;
        metadata?: Record<string, unknown>;
      }
    ) => {
      return baseAPIInstance.post(`/asset-link/${id}/events`, data);
    },
  },
  videos: {
    upload: (file: FormData) => {
      return baseAPIInstance.post('/video/upload', file) as Promise<
        AxiosResponse<TUploadVideoResponse>
      >;
    },
    uploadWithUrl: (link: string) => {
      return baseAPIInstance.post(`/video/upload-by-url`, undefined, {
        params: {
          url: link,
        },
      }) as Promise<AxiosResponse<TUploadByLinkResponse>>;
    },
    addWithoutSave: (link: string) => {
      return baseAPIInstance.post(
        '/video/add-by-url-without-upload',
        undefined,
        {
          params: {
            url: link,
          },
        }
      ) as Promise<AxiosResponse<TUploadByLinkResponse>>;
    },
    getAll: () => {
      return baseAPIInstance.get('/video/list') as Promise<
        AxiosResponse<TCreatedVideo[]>
      >;
    },
    create: (data: TVideoCreatePayload) => {
      return baseAPIInstance.post('videos/setting', data);
    },
    createVideo: (data: TVideoCreateForm) => {
      return baseAPIInstance.post('video/create', data) as Promise<
        AxiosResponse<string>
      >;
    },
    editVideo: (data: Partial<TVideoCreateForm>, videoId: string) => {
      return baseAPIInstance.patch(`video/${videoId}`, data) as Promise<
        AxiosResponse<string>
      >;
    },
    createPreview: (data: TCreatePreviewPayload) => {
      return baseAPIInstance.post('video/createPreview', data) as Promise<
        AxiosResponse<TCreatePreviewResponse>
      >;
    },
    getById: (id: string) => {
      return baseAPIInstance.get(`video/${id}`) as Promise<
        AxiosResponse<TVideoResponse>
      >;
    },
    deleteById: (id: string) => {
      return baseAPIInstance.delete(`video/${id}`) as Promise<AxiosResponse>;
    },
    deleteFromS3: (key: string) => {
      return baseAPIInstance.delete(
        `video/from-s3?key=${key}`
      ) as Promise<AxiosResponse>;
    },
    createLink: (
      data: { videoId: string; statisticsId?: string },
      form: TLinkForm,
      description?: string
    ) => {
      return baseAPIInstance.post('link', {
        videoId: data.videoId,
        ...(data.statisticsId ? { statisticsId: data.statisticsId } : {}),
        description: description ?? '',
        videoSetting: {
          duration: 0,
          ...form,
        },
      });
    },
    editLink: (
      data: { videoId: string; statisticsId?: string },
      form: TLinkForm,
      linkId: string,
      description?: string
    ) => {
      return baseAPIInstance.patch(`link/${linkId}`, {
        videoId: data.videoId,
        ...(data.statisticsId ? { statisticsId: data.statisticsId } : {}),
        description: description ?? '',
        videoSetting: {
          duration: 0,
          ...form,
        },
      });
    },
    deleteLink: (id: string) => {
      return baseAPIInstance.delete(`link/${id}`) as Promise<AxiosResponse>;
    },
    getLinksByVideo: (videoId: string) => {
      return baseAPIInstance.get(`link/list-by-video/${videoId}`) as Promise<
        AxiosResponse<TLinkListed[]>
      >;
    },
    getViewed: () => {
      return baseAPIInstance.get('history/videos') as Promise<
        AxiosResponse<TVideoResponse[]>
      >;
    },
    getAllLinks: () => {
      return baseAPIInstance.get('/link/self') as Promise<
        AxiosResponse<TLinkListed[]>
      >;
    },
    getLinChunkskStat: (id: string, userId: string) => {
      if (userId)
        return baseAPIInstance.get(
          `/chunks/heat-map?linkId=${id}&userId=${userId}`
        ) as Promise<AxiosResponse<TLinkChunkStat[]>>;

      return baseAPIInstance.get(`/chunks/heat-map?linkId=${id}`) as Promise<
        AxiosResponse<TLinkChunkStat[]>
      >;
    },
    getLinkStat: (id: string, userId: string) => {
      if (userId)
        return baseAPIInstance.get(
          `/stats/user-stats-by-link-for-owner?linkId=${id}&userId=${userId}`
        ) as Promise<AxiosResponse<TLinkStat>>;

      return baseAPIInstance.get(
        `/stats/link-stats-by-id?linkId=${id}`
      ) as Promise<AxiosResponse<TLinkStat>>;
    },
  },
  playlists: {
    create: (data: TPlayListCreateForm) => {
      return baseAPIInstance.post('playlist', {
        ...data,
        videoIds: [],
      }) as Promise<AxiosResponse<TPlayListItem>>;
    },
    getAll: (params: TPlaylistQueryParam) => {
      return baseAPIInstance.get('playlist/list', {
        params,
      }) as Promise<AxiosResponse<TListedPlaylist[]>>;
    },
    getById: (id: string) => {
      return baseAPIInstance.get(`playlist/${id}/progress`) as Promise<
        AxiosResponse<TPlayListItem>
      >;
    },
    getItem: (id: string, index: number) => {
      return baseAPIInstance.get(`playlist/${id}/item/${index}`) as Promise<
        AxiosResponse<TPlaylistContentItem>
      >;
    },
    edit: (data: TPlayListEditForm, id: string) => {
      return baseAPIInstance.patch(`playlist/${id}`, data) as Promise<
        AxiosResponse<TListedPlaylist>
      >;
    },
    delete: (id: string) => {
      return baseAPIInstance.delete(`playlist/${id}`) as Promise<AxiosResponse>;
    },
    getViewed: () => {
      return baseAPIInstance.get('history/playlists') as Promise<
        AxiosResponse<TListedPlaylist[]>
      >;
    },
    getAssigned: () => {
      const now = Date.now();
      if (assignedPlaylistsCache && assignedPlaylistsCache.expiresAt > now) {
        return Promise.resolve(assignedPlaylistsCache.response);
      }

      if (assignedPlaylistsRequest) {
        return assignedPlaylistsRequest;
      }

      assignedPlaylistsRequest = baseAPIInstance
        .get('playlist/assigned/list')
        .then((response) => {
          const typedResponse = response as AxiosResponse<TListedPlaylist[]>;
          assignedPlaylistsCache = {
            response: typedResponse,
            expiresAt: Date.now() + ASSIGNED_PLAYLISTS_CACHE_MS,
          };
          return typedResponse;
        })
        .finally(() => {
          assignedPlaylistsRequest = null;
        });

      return assignedPlaylistsRequest;
    },
    getStudents: () => {
      return baseAPIInstance.get('playlist/students/list') as Promise<
        AxiosResponse<TUser[]>
      >;
    },
  },
  auth: {
    login: (data: TLoginForm) => {
      return baseAPIInstance.post('auth/login', data) as Promise<
        AxiosResponse<TAuthResponse>
      >;
    },
    registration: (data: TLoginForm) => {
      return baseAPIInstance.post('auth/registration', data) as Promise<
        AxiosResponse<TAuthResponse>
      >;
    },
    studentLogin: (data: TLoginForm) => {
      return baseAPIInstance.post('auth/login/student', data) as Promise<
        AxiosResponse<TAuthResponse>
      >;
    },
    studentRegistration: (data: TLoginForm) => {
      return baseAPIInstance.post('auth/registration/student', data) as Promise<
        AxiosResponse<TAuthResponse>
      >;
    },
    forgotPassword: (data: TForgotLoginForm) => {
      return baseAPIInstance.post('auth/restore-password', data);
    },
    forgotPasswordComplete: (data: TForgotPasswordCompletePayload) => {
      return baseAPIInstance.patch('auth/change-password-from-email', data);
    },
  },
  me: {
    getMyAccount: () => {
      return baseAPIInstance.get('auth/me') as Promise<
        AxiosResponse<TMeResponse>
      >;
    },
    // changeAvatar: (data: { profilePhotoUrl: string }) => {
    //   return baseAPIInstance.patch(
    //     'me/change-profile-photo',
    //     data
    //   ) as Promise<AxiosResponse>;
    // },
    patchMyUser: (
      data: Partial<{
        firstName: string | null;
        middleName: string | null;
        lastName: string | null;
      }>
    ) => {
      return baseAPIInstance.patch('me', data) as Promise<AxiosResponse>;
    },
    changePassword: (data: { oldPassword: string; newPassword: string }) => {
      return baseAPIInstance.patch(
        'me/change-password',
        data
      ) as Promise<AxiosResponse>;
    },
    changeEmail: (email: string) => {
      return baseAPIInstance.patch('me/change-email', {
        email,
      }) as Promise<AxiosResponse>;
    },
    deleteAccount: () => {
      return baseAPIInstance.delete('me/delete');
    },
  },
  users: {
    getAll: (
      params: { skip?: number; take?: number } = {
        skip: 0,
        take: 50,
      }
    ) => {
      return baseAPIInstance.get('/users', {
        params,
      }) as Promise<AxiosResponse<TUser[]>>;
    },
    updateRole: (id: string, role: number) => {
      return baseAPIInstance.patch(`/users/${id}/role`, {
        role,
      }) as Promise<AxiosResponse<TUser>>;
    },
    updatePermissions: (
      id: string,
      permissions: {
        canAssignTeacherRole?: boolean;
        studentAccess?: boolean;
      }
    ) => {
      return baseAPIInstance.patch(
        `/users/${id}/permissions`,
        permissions
      ) as Promise<AxiosResponse<TUser>>;
    },
  },
  analytics: {
    getAll: (filters?: TAnalyticsFilter) => {
      return baseAPIInstance.get('/stats/all', {
        params: {
          ...filters,
        },
      }) as Promise<AxiosResponse<TAnalyticsResponse>>;
    },
    getDynamics: (filters: TDynamicFilters) => {
      return baseAPIInstance.get('/stats/dynamics', {
        params: {
          ...filters,
        },
      }) as Promise<AxiosResponse<TDynamicData>>;
    },
    downloadExcel: (data: TExcelDownloadPayload) => {
      return baseAPIInstance.get('/excel/download-stats', {
        params: data,
        responseType: 'blob',
      }) as Promise<AxiosResponse<Blob>>;
    },
  },

  payment: {
    getTariffPlansAsync: (period: string | undefined) => {
      return baseAPIInstance.get('/tariff-plan', {
        params: {
          period,
        },
      }) as Promise<AxiosResponse<TFinancePlan[]>>;
    },

    buyTariffPlanAsync: (tariffId: string) => {
      return baseAPIInstance.patch('/tariff-plan/change', {
        tariffId,
        billing_policy: 'IMMEDIATE',
      }) as Promise<AxiosResponse<TPaymentData>>;
    },
    getInvoiceListAsync: () => {
      return baseAPIInstance.post('/payment-api/get-invoices', {}) as Promise<
        AxiosResponse<TInvoice[]>
      >;
    },
  },
};
